import posthog from "posthog-js"

/**
 * Funnel: login → onboarding → dashboard with plan
 * - login_success: OTP verified, session established (login page)
 * - onboarding_started: user reached onboarding chat (mounts on /onboarding)
 * - onboarding_completed: plan created (graduate success in onboarding)
 * - dashboard_loaded: user landed on dashboard (layout only allows access with plan)
 */
export type AnalyticsEvent =
  | "user_signed_up"
  | "login_success"
  | "otp_send_requested"
  | "otp_send_success"
  | "otp_send_failed"
  | "login_no_account"
  | "onboarding_started"
  | "onboarding_completed"
  | "onboarding_go_to_dashboard_clicked"
  | "onboarding_auto_redirect_on_existing_plan"
  | "onboarding_finalize_timeout"
  | "onboarding_finalize_finished"
  // Onboarding v2 (structured goal selection)
  | "onboarding_v2_goal_viewed"
  | "onboarding_v2_goal_selected"
  | "onboarding_v2_distance_selected"
  | "onboarding_v2_improve_focus_selected"
  | "onboarding_v2_routine_weeks_selected"
  | "onboarding_v2_continued_to_chat"
  | "onboarding_v2_change_goal_clicked"
  | "onboarding_v2_change_focus_clicked"
  | "onboarding_v2_seeded"
  | "dashboard_loaded"
  | "dashboard_plan_bootstrap_triggered"
  | "week_auto_generate_triggered"
  | "chat_message_sent"
  | "chat_action_generate_week_clicked"
  | "week_generate_clicked"
  | "week_generated_success"
  | "week_generation_failed"
  | "week_update_requested"
  | "week_updated_success"
  | "send_to_garmin_clicked"
  | "send_to_garmin_success"
  | "send_to_garmin_failed"
  | "import_from_garmin_clicked"
  | "refresh_from_garmin_clicked"
  | "import_from_garmin_success"
  | "import_from_garmin_failed"
  | "quick_workout_clicked"
  | "chat_update_day_clicked"
  | "chat_send_to_garmin_clicked"
  | "feedback_opened"
  | "feedback_submitted"
  // Athlete profile capture
  | "onboarding_about_you_viewed"
  | "onboarding_about_you_submitted"
  // Home page setup card: the first thing someone does in it, each step
  // ticked by hand, the Claude or ChatGPT links opened, and copying the address.
  | "started_setup"
  | "ticked_setup_step"
  | "opened_setup_link"
  | "copied_connector_url"
  // A training plan page's "Put this plan on your Garmin", at the top or in the side panel.
  | "clicked_plan_cta"
  // Step 4 of setup: which first ask people copy.
  | "copied_first_prompt"
  // The "get the most out of Daash" guide: which prompts people copy, by section.
  | "copied_guide_prompt"
  // The plan card playground: changing the example plan, and pressing its buttons.
  | "plan_playground_changed"
  | "plan_playground_action"
  // Temporary debug instrumentation for dashboard timeline loading.
  | "timeline_load_failed"

export function trackEvent(
  name: AnalyticsEvent,
  properties?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return
  try {
    posthog.capture(name, properties)
  } catch {
    // fail silently — analytics must never break the app
  }
}

export function captureError(
  error: Error,
  additionalProperties?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return
  try {
    posthog.captureException(error, additionalProperties)
  } catch {
    // fail silently — analytics must never break the app
  }
}
